import { of } from 'rxjs';
import { Component, OnDestroy, OnInit } from "@angular/core";
import { Location } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { PlayerMatchHistoryGameResult, PlayerMatchHistoryItem, PlayerMatchHistoryParticipant } from "../../shared/models/player";
import { PlayerService } from "../../shared/services/player.service";
import { navigateBackOrFallback, ScreenInitialLoading } from "../../shared/screen-shell/screen-navigation.utils";

@Component({
  selector: "app-player-match-history",
  templateUrl: "./player-match-history.component.html",
  styleUrls: ["../player.component.css", "../../event/event-history/event-history.component.css"],
  standalone: false
})
export class PlayerMatchHistoryComponent implements OnInit, OnDestroy {

  public playerId: string;
  public playerName: string;
  public items: PlayerMatchHistoryItem[] = [];
  public loading = false;
  public errorMsg: string;
  public forbidden = false;
  public page = 0;
  public pageSize = 20;
  public hasMore = false;
  public sharedOnly = false;
  public showInitialLoading = false;
  private currentPlayerId: string;
  private contextEventId: string;
  private returnToPlayerDetail = false;
  private returnPlayerId: string;
  private returnEventId: string;
  private readonly initialLoading = new ScreenInitialLoading(300, (visible) => this.showInitialLoading = visible);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private playerService: PlayerService,
    private translate: TranslateService,
    private location: Location
  ) {}

  public ngOnInit(): void {
    this.playerId = this.route.snapshot.params["playerId"];
    this.contextEventId = this.route.snapshot.queryParamMap.get("contextEventId");
    this.returnToPlayerDetail = this.route.snapshot.queryParamMap.get("returnToPlayerDetail") === "true";
    this.returnPlayerId = this.route.snapshot.queryParamMap.get("returnPlayerId");
    this.returnEventId = this.route.snapshot.queryParamMap.get("returnEventId");
    const currentPlayer = this.playerService.peekCurrentPlayer();
    this.currentPlayerId = currentPlayer && currentPlayer.id ? currentPlayer.id : null;
    this.loadPage(0);
  }

  public ngOnDestroy(): void {
    this.initialLoading.destroy();
  }

  public goBack(): void {
    this.prepareReturnToPlayerDetail();
    navigateBackOrFallback(this.location, this.router, this.playerId ? ["/profile", this.playerId] : ["/event-list"]);
  }

  public loadMore(): void {
    if (this.loading || !this.hasMore) {
      return;
    }
    this.loadPage(this.page + 1);
  }

  public canFilterSharedOnly(): boolean {
    return !!(this.currentPlayerId && this.playerId && this.currentPlayerId !== this.playerId);
  }

  public setSharedOnly(sharedOnly: boolean): void {
    if (this.sharedOnly === sharedOnly || this.loading) {
      return;
    }
    this.sharedOnly = sharedOnly;
    this.loadPage(0);
  }

  public headerTitle(): string {
    return this.translate.instant("players.matchHistory");
  }

  public headerSubtitle(): string {
    return this.currentPlayerId && this.playerId && this.currentPlayerId !== this.playerId ? this.playerName : null;
  }

  public isContentLoading(): boolean {
    return this.showInitialLoading && this.items.length === 0;
  }

  public historyDateTimeLabel(item: PlayerMatchHistoryItem): string {
    if (!item || !item.nextOccurrenceTimestamp) {
      return "-";
    }

    const date = new Date(item.nextOccurrenceTimestamp);
    const locale = this.translate.currentLang === "hr" ? "hr-HR" : "en-US";
    const timeZone = item.nextOccurrenceTimestampZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const currentYear = new Date().getFullYear();
    const eventYear = Number(new Intl.DateTimeFormat("en-US", { year: "numeric", timeZone }).format(date));
    const dateLabel = new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
      year: eventYear === currentYear ? undefined : "numeric",
      timeZone
    }).format(date).toUpperCase();
    const timeLabel = new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone
    }).format(date);

    return `${dateLabel} ${this.translate.currentLang === "hr" ? "u" : "at"} ${timeLabel}`;
  }

  public historyDateDay(item: PlayerMatchHistoryItem): string {
    const date = this.historyDate(item);
    if (!date) {
      return "-";
    }
    return new Intl.DateTimeFormat("en-US", {
      day: "2-digit",
      timeZone: this.historyTimeZone(item)
    }).format(date);
  }

  public historyDateMonthYear(item: PlayerMatchHistoryItem): string {
    const date = this.historyDate(item);
    if (!date) {
      return "-";
    }
    const locale = this.translate.currentLang === "hr" ? "hr-HR" : "en-US";
    const currentYear = new Date().getFullYear();
    const eventYear = Number(new Intl.DateTimeFormat("en-US", { year: "numeric", timeZone: this.historyTimeZone(item) }).format(date));
    return new Intl.DateTimeFormat(locale, {
      month: "short",
      year: eventYear === currentYear ? undefined : "numeric",
      timeZone: this.historyTimeZone(item)
    }).format(date).replace(".", "").toUpperCase();
  }

  public historyTimeLabel(item: PlayerMatchHistoryItem): string {
    const date = this.historyDate(item);
    if (!date) {
      return "-";
    }
    return new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: this.historyTimeZone(item)
    }).format(date);
  }

  public historyWeekdayLabel(item: PlayerMatchHistoryItem): string {
    const date = this.historyDate(item);
    if (!date) {
      return "-";
    }
    const locale = this.translate.currentLang === "hr" ? "hr-HR" : "en-US";
    return new Intl.DateTimeFormat(locale, {
      weekday: "short",
      timeZone: this.historyTimeZone(item)
    }).format(date).replace(".", "");
  }

  public historyLocation(item: PlayerMatchHistoryItem): string {
    return item && (item.playSpaceName || item.locationName) ? (item.playSpaceName || item.locationName) : "-";
  }

  public historyMatchLocation(item: PlayerMatchHistoryItem): string {
    const namedItem = item as PlayerMatchHistoryItem & { name?: string };
    const matchName = namedItem && namedItem.name ? namedItem.name : "";
    const location = this.historyLocation(item);
    if (matchName && location && location !== "-") {
      return `${matchName} at ${location}`;
    }
    return matchName || location;
  }

  public historyLocationMapUrl(item: PlayerMatchHistoryItem): string {
    const location = this.historyLocation(item);
    return location !== "-" ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}` : null;
  }

  public historyConfirmedCount(item: PlayerMatchHistoryItem): number {
    return item && item.confirmedCount != null ? Number(item.confirmedCount) || 0 : 0;
  }

  public historyMaxPlayers(item: PlayerMatchHistoryItem): number {
    return item && item.maxPlayers != null ? Number(item.maxPlayers) || 0 : 0;
  }

  public historyAttendanceLabel(item: PlayerMatchHistoryItem): string {
    const count = this.historyConfirmedCount(item);
    const max = this.historyMaxPlayers(item) || "-";
    if (item && item.played === true) {
      return `${count}/${max} ${this.translate.instant("events.playersShowedUp")}`;
    }
    return this.translate.instant("events.matchNotPlayedConfirmedCount", { count, max });
  }

  public hasGameResults(item: PlayerMatchHistoryItem): boolean {
    return !!(item && item.gameResults && item.gameResults.length > 0);
  }

  public hasScore(game: PlayerMatchHistoryGameResult): boolean {
    return !!(game && game.whiteScore != null && game.blackScore != null);
  }

  public shouldShowItemOutcome(item: PlayerMatchHistoryItem): boolean {
    return !item || !item.gameResults || item.gameResults.length <= 1;
  }

  public shouldShowRoundOutcome(item: PlayerMatchHistoryItem): boolean {
    return !!(item && item.gameResults && item.gameResults.length > 1);
  }

  public primaryGame(item: PlayerMatchHistoryItem): PlayerMatchHistoryGameResult {
    return this.hasGameResults(item) ? item.gameResults[0] : null;
  }

  public historyItemOutcomeLabel(item: PlayerMatchHistoryItem): string {
    const game = this.primaryGame(item);
    if (!game || !this.hasScore(game)) {
      return this.translate.instant("players.matchHistoryNoScore");
    }
    return this.historyRoundOutcomeLabel(game);
  }

  public historyItemOutcomeKey(item: PlayerMatchHistoryItem): string {
    const game = this.primaryGame(item);
    if (!game || !this.hasScore(game)) {
      return "no-score";
    }
    return this.historyRoundOutcomeKey(game);
  }

  public shouldShowRoster(item: PlayerMatchHistoryItem, game: PlayerMatchHistoryGameResult): boolean {
    return !!(item && item.canViewRoster && game && ((game.whitePlayerIds && game.whitePlayerIds.length > 0) || (game.blackPlayerIds && game.blackPlayerIds.length > 0)));
  }

  public resultRoundLabel(game: PlayerMatchHistoryGameResult): string {
    const roundNumber = game && game.roundNumber ? game.roundNumber : 1;
    return `${this.translate.instant("events.gameRound")} ${roundNumber}`.trim();
  }

  public historyRoundOutcomeLabel(game: PlayerMatchHistoryGameResult): string {
    if (!game || game.playerOutcome === "NOTHING") {
      return this.translate.instant("events.roundNotPlayed");
    }
    if (game.playerOutcome === "WON") {
      return this.translate.instant("events.roundWon");
    }
    if (game.playerOutcome === "LOST") {
      return this.translate.instant("events.roundLost");
    }
    return this.translate.instant("events.roundDraw");
  }

  public historyRoundOutcomeKey(game: PlayerMatchHistoryGameResult): string {
    if (!game || !game.playerOutcome) {
      return "nothing";
    }
    return game.playerOutcome.toLowerCase();
  }

  public gameTeamPlayers(item: PlayerMatchHistoryItem, game: PlayerMatchHistoryGameResult, jersey: "WHITE" | "BLACK"): PlayerMatchHistoryParticipant[] {
    if (!item || !game || !item.canViewRoster) {
      return [];
    }
    const ids = jersey === "BLACK" ? (game.blackPlayerIds || []) : (game.whitePlayerIds || []);
    return ids
      .map((playerId) => this.findParticipant(item, playerId))
      .filter((participant) => !!participant);
  }

  public playerDisplayName(participant: PlayerMatchHistoryParticipant): string {
    return participant ? (participant.nickName || participant.name || "-") : "-";
  }

  public playerInitials(participant: PlayerMatchHistoryParticipant): string {
    const name = this.playerDisplayName(participant);
    return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join("") || "?";
  }

  private initialPageConsumed = false;

  private loadPage(page: number): void {
    const prepared = !this.initialPageConsumed && this.route.snapshot.data['screen'];
    this.initialPageConsumed = true;
    this.beginLoading(page);
    this.errorMsg = null;
    this.forbidden = false;
    (prepared ? of(prepared) : this.playerService.getPlayerMatchHistory(this.playerId, page, this.pageSize, this.sharedOnly, this.contextEventId)).subscribe(
      (response) => {
        this.playerName = response.playerNickName || response.playerName || this.playerName;
        this.page = response.page;
        this.hasMore = response.hasMore;
        this.items = page === 0 ? (response.items || []) : this.items.concat(response.items || []);
        this.finishLoading();
      },
      (error) => {
        this.finishLoading();
        this.forbidden = error && error.status === 403;
        this.errorMsg = this.forbidden
          ? this.translate.instant("players.matchHistoryForbidden")
          : this.translate.instant("players.matchHistoryLoadFailed");
      }
    );
  }

  private findParticipant(item: PlayerMatchHistoryItem, playerId: string): PlayerMatchHistoryParticipant {
    return (item.participants || []).find((participant) => participant && participant.id === playerId);
  }

  private historyDate(item: PlayerMatchHistoryItem): Date {
    return item && item.nextOccurrenceTimestamp ? new Date(item.nextOccurrenceTimestamp) : null;
  }

  private historyTimeZone(item: PlayerMatchHistoryItem): string {
    return item && item.nextOccurrenceTimestampZone ? item.nextOccurrenceTimestampZone : Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  private prepareReturnToPlayerDetail(): void {
    if (!this.returnToPlayerDetail || !this.returnPlayerId) {
      return;
    }
    localStorage.setItem("openPlayerDetailForPlayerId", this.returnPlayerId);
    if (this.returnEventId) {
      localStorage.setItem("openPlayerDetailForEventId", this.returnEventId);
    }
  }

  private beginLoading(page: number): void {
    this.loading = true;
    if (page === 0) {
      this.hasMore = false;
    }
    this.initialLoading.begin(page === 0 && this.items.length === 0, () => this.loading);
  }

  private finishLoading(): void {
    this.loading = false;
    this.initialLoading.finish();
  }
}
